/**
 * Google Ads connection settings — ADMIN ONLY (ConnectionsHub hides this tab).
 *
 * Every keyword request runs through our own manager account, so a personal
 * connection from a non-admin could only ever be denied. Instead one admin
 * connects, marks it the "house" connection, and grants named users the right to
 * borrow it. See jcseo/keywords/google_ads_access.py for the full rationale and
 * the path to real per-user connections once we manage ads.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import type { GoogleAdsHouseGrant } from '../types';

export default function GoogleAdsSettings() {
  const [searchParams] = useSearchParams();
  const [connected, setConnected] = useState(false);
  const [authFailedAt, setAuthFailedAt] = useState<string | null>(null);
  const [isHouse, setIsHouse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [grants, setGrants] = useState<GoogleAdsHouseGrant[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [savingHouse, setSavingHouse] = useState(false);
  const [savingGrantFor, setSavingGrantFor] = useState<number | null>(null);

  const loadGrants = useCallback(async () => {
    setGrantsLoading(true);
    try {
      setGrants(await api.getGoogleAdsHouseGrants());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setGrantsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const status = await api.getGoogleAdsStatus();
        setConnected(status.connected);
        setAuthFailedAt(status.auth_failed_at);
        setIsHouse(status.is_house);
        if (status.is_house) await loadGrants();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load status');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [loadGrants]);

  const handleToggleHouse = async (enabled: boolean) => {
    setSavingHouse(true);
    setError('');
    try {
      const status = await api.setGoogleAdsHouse(enabled);
      setIsHouse(status.is_house);
      if (status.is_house) await loadGrants();
      setSuccessMessage(
        enabled
          ? 'Sharing enabled - grant access to specific users below.'
          : 'Sharing turned off. Nobody else can use this connection now.'
      );
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing');
    } finally {
      setSavingHouse(false);
    }
  };

  const handleToggleGrant = async (userId: number, granted: boolean) => {
    setSavingGrantFor(userId);
    setError('');
    try {
      const updated = await api.setGoogleAdsHouseGrant(userId, granted);
      setGrants((prev) =>
        prev.map((g) => (g.user_id === userId ? { ...g, granted: updated.granted } : g))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update access');
    } finally {
      setSavingGrantFor(null);
    }
  };

  // Handle redirect back from Google OAuth
  useEffect(() => {
    const success = searchParams.get('success');
    const errorParam = searchParams.get('error');
    if (success === 'true') {
      setConnected(true);
      setSuccessMessage('Google Ads connected successfully.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (errorParam) {
      setError(`Google Ads connection failed: ${errorParam}`);
    }
  }, [searchParams]);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      const { url } = await api.getGoogleAdsAuthUrl();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get auth URL');
      setConnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccessMessage('');
    try {
      const { ok, detail } = await api.testGoogleAdsConnection();
      if (ok) {
        setAuthFailedAt(null);
        setSuccessMessage(detail);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(`Connection test failed: ${detail}`);
        // Re-sync the failed flag the backend may have just set.
        try {
          const { auth_failed_at } = await api.getGoogleAdsStatus();
          setAuthFailedAt(auth_failed_at);
        } catch {
          /* keep the primary error visible */
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError('');
    try {
      await api.disconnectGoogleAds();
      setConnected(false);
      setSuccessMessage('Google Ads disconnected.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Google Ads</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Connect a Google account with access to the Lumnian manager account to enable
          keyword metrics. You can then share this one connection with specific users -
          they never see or handle the credentials.
        </p>
      </div>

      {/* Google's OAuth verification requires the privacy policy to be reachable
          from the point of consent, and it spells out exactly which Ads API calls
          this connection makes. Keep this in view of the Connect button. */}
      <div className="rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 p-4 text-xs text-gray-600 dark:text-gray-400">
        We request a single scope,{' '}
        <code className="rounded bg-gray-200 dark:bg-gray-700 px-1 py-0.5">
          .../auth/adwords
        </code>
        , and use it only to look up keyword ideas and search-volume estimates for the seeds
        you supply. We never read your campaigns, ads, budgets or billing, and we never change
        anything in your account. See our{' '}
        <a
          href="https://lumnian.com/privacy#google"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://lumnian.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Terms of Service
        </a>
        . You can revoke access at any time here or at{' '}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          myaccount.google.com/permissions
        </a>
        .
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}
      {successMessage && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4 text-sm text-green-700 dark:text-green-300">{successMessage}</div>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : connected ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  authFailedAt ? 'bg-amber-500' : 'bg-green-500'
                }`}
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {authFailedAt ? 'Connected - needs attention' : 'Connected'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {authFailedAt
                    ? `A recent Google Ads call failed authentication (${new Date(
                        authFailedAt
                      ).toLocaleString()}). Test the connection, and reconnect if it fails.`
                    : 'Your Google Ads account is linked and keyword metrics are being ingested.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleTest}
                disabled={testing}
                className="rounded-md border border-blue-300 dark:border-blue-700 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
              >
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              {authFailedAt && (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {connecting ? 'Redirecting…' : 'Reconnect'}
                </button>
              )}
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="rounded-md border border-red-300 dark:border-red-700 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
              >
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Not connected</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Connect your Google Ads account to start ingesting keyword metrics.
                </p>
              </div>
            </div>
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {connecting ? 'Redirecting…' : 'Connect Google Ads'}
            </button>
          </div>
        )}
      </div>

      {connected && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Share this connection
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Lets chosen users pull keyword volumes through this connection without
                connecting anything themselves. Credentials stay on the server - nobody
                else can see, test, or disconnect it.
              </p>
            </div>
            <label className="flex shrink-0 items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isHouse}
                disabled={savingHouse}
                onChange={(e) => handleToggleHouse(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
              />
              {savingHouse ? 'Saving…' : 'Shared'}
            </label>
          </div>

          {isHouse && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              {grantsLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading users…</p>
              ) : grants.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No other active users yet.
                </p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {grants.map((g) => (
                    <li
                      key={g.user_id}
                      className="flex items-center justify-between gap-4 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-900 dark:text-gray-100">
                          {g.username}
                        </p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {g.email}
                          {g.has_own_connection &&
                            ' · has their own connection, which takes precedence'}
                        </p>
                      </div>
                      <label className="flex shrink-0 items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <input
                          type="checkbox"
                          checked={g.granted}
                          disabled={savingGrantFor === g.user_id}
                          onChange={(e) => handleToggleGrant(g.user_id, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                        />
                        {savingGrantFor === g.user_id ? 'Saving…' : 'Access'}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
